import { useEffect, useState } from 'react';
import { RotateCcw, Target } from 'lucide-react';
import api from '../../../services/api';
import { pingFeature } from '../../../hooks/useFeaturePing';

/**
 * Reprise après une absence (« Bon retour »), intégrée au dock « Ta prochaine étape ».
 *
 * Elle s'active quand le candidat n'a répondu à aucune question de la thématique depuis
 * RETOUR_JOURS jours et qu'il a du travail en attente (révisions dues ou erreurs). Le dock
 * change alors d'en-tête (welcomeHeading) et affiche, sous la recommandation guidée, deux
 * raccourcis de reprise courte (WelcomeBackShortcuts) : révisions express et erreurs du
 * sujet le plus fautif. Les chiffres viennent des mêmes endpoints que les séances lancées
 * (sm2-due.php?summary=1, errors.php), pour n'annoncer rien que la séance ne tienne.
 *
 * « Plus tard » masque la reprise MASQUAGE_JOURS jours (stockage local, par thématique).
 */
const RETOUR_JOURS = 7;
const MASQUAGE_JOURS = 7;
const EXPRESS = 10;

function masquee(scopeId) {
    try {
        const t = Number(localStorage.getItem(`monquiz_wb_${scopeId}`) || 0);
        return t > 0 && Date.now() - t < MASQUAGE_JOURS * 86400000;
    } catch { return false; }
}
function masquer(scopeId) {
    try { localStorage.setItem(`monquiz_wb_${scopeId}`, String(Date.now())); } catch { /* stockage indisponible */ }
}

// « FABIEN » ou « jean-pierre » → « Fabien », « Jean-Pierre »
function formatPrenom(p) {
    return String(p || '').trim().toLowerCase().replace(/(^|[\s-])(\p{L})/gu, (m, sep, l) => sep + l.toUpperCase());
}

function depuis(days) {
    if (days < 30) return `${days} jours`;
    if (days < 60) return 'plus d’un mois';
    if (days < 365) return `${Math.floor(days / 30)} mois`;
    return days < 730 ? 'plus d’un an' : `${Math.floor(days / 365)} ans`;
}

/** Charge l'état de reprise ; scopeId nul = désactivé. */
export function useWelcomeBack(scopeId) {
    const [info, setInfo] = useState(null);

    useEffect(() => {
        setInfo(null);
        if (!scopeId || masquee(scopeId)) return undefined;
        let annule = false;
        (async () => {
            try {
                const { data: wb } = await api.get('/user/welcome-back.php', { params: { scope_id: scopeId } });
                const days = wb?.data?.days_away;
                if (days === null || days === undefined || days < RETOUR_JOURS) return;
                const [sm2, err] = await Promise.all([
                    api.get('/qcm/sm2-due.php', { params: { scope_id: scopeId, summary: 1 } }).catch(() => null),
                    api.post('/qcm/errors.php', { scope_id: scopeId, mode: 'never', page: 1 }).catch(() => null),
                ]);
                const s = sm2?.data?.data || {};
                const due = (s.due_today || 0) + (s.due_overdue || 0);
                const e = err?.data?.data || {};
                const sujets = (e.by_sujet || []).filter(x => (x.playable ?? x.count) > 0)
                    .sort((a, b) => (b.playable ?? b.count) - (a.playable ?? a.count));
                const errTotal = e.playable_total ?? e.total_questions ?? 0;
                if (annule || (due <= 0 && errTotal <= 0)) return;
                setInfo({ days, due, errTotal, top: sujets[0] || null });
                pingFeature('welcome_back_shown');
            } catch { /* reprise facultative : silence */ }
        })();
        return () => { annule = true; };
    }, [scopeId]);

    const dismiss = () => { if (scopeId) masquer(scopeId); setInfo(null); };
    return { info, dismiss };
}

/** Porte le hook pour une page qui a des retours anticipés : children(wb). */
export function WelcomeBackGate({ scopeId, children }) {
    return children(useWelcomeBack(scopeId));
}

/** En-tête du dock en mode reprise. */
export function welcomeHeading(info, prenom, dismiss) {
    const p = formatPrenom(prenom);
    const reste = [
        info.due > 0 ? `${info.due} question${info.due > 1 ? 's' : ''} à revoir` : null,
        info.errTotal > 0 ? `${info.errTotal} erreur${info.errTotal > 1 ? 's' : ''} à retravailler` : null,
    ].filter(Boolean).join(' et ');
    return {
        eyebrow: `Bon retour${p ? `, ${p}` : ''}`,
        title: 'On reprend en douceur ?',
        sub: `Ta dernière séance remonte à ${depuis(info.days)}${reste ? ` : ${reste} t’attendent` : ''}. Inutile de tout rattraper d’un coup, une séance courte suffit pour repartir.`,
        dismissLabel: 'Plus tard',
        onDismiss: () => { pingFeature('welcome_back_dismiss'); dismiss(); },
    };
}

/** Raccourcis de reprise, sous la recommandation guidée. */
export default function WelcomeBackShortcuts({ info, dismiss, onSm2, onErrors }) {
    if (!info) return null;
    const nbExpress = Math.min(EXPRESS, info.due);
    const topNb = info.top ? (info.top.playable ?? info.top.count) : 0;
    if (nbExpress <= 0 && !info.top) return null;

    return (
        <div className="mode-dock__wb" role="group" aria-label="Reprendre rapidement">
            <span className="mode-dock__wb-label">Ou reprends en quelques minutes</span>
            <div className="mode-dock__wb-chips">
                {nbExpress > 0 && (
                    <button type="button" className="mode-dock__wb-chip" onClick={() => { pingFeature('welcome_back_sm2'); dismiss(); onSm2(nbExpress); }}>
                        <RotateCcw size={15} aria-hidden="true" />
                        <span>{nbExpress} révisions express</span>
                    </button>
                )}
                {info.top && (
                    <button type="button" className="mode-dock__wb-chip" onClick={() => { pingFeature('welcome_back_errors'); dismiss(); onErrors(info.top.sous_sujet_id); }}>
                        <Target size={15} aria-hidden="true" />
                        <span>Mes erreurs en {info.top.label}</span>
                        <b>{topNb}</b>
                    </button>
                )}
            </div>
        </div>
    );
}
